/* eslint-disable camelcase */
import { useState, useCallback } from 'react';

import useNotifications from '../../../hooks/useNotifications';
import { createClaimContextFromShipment } from './createClaimContext';
import container from '../../../container';
import { useSelectedShopId } from '../../../hooks/useSelectedShop';
import { ClaimContext } from '../../parcel-finder/claims/types';
import { useClaimsShopSettings } from '../../parcel-finder/ClaimsShopSettingsContext';
import { Flags } from './useSubmitClaim';

interface NonClaimableMessages {
  [key: string]: string;
}

const nonClaimableMessages: NonClaimableMessages = {
  claim_exists_for_shipment_id: 'CLAIM_ALREADY_EXISTS_FOR_SHIPMENT',
  claims_not_enabled: 'CLAIM_NOT_ENABLED',
  not_allowed_7s_shipment: 'CLAIMS_CENTER_CASH_ON_DELIVERY_IS_7S_SHIPMENT',
  not_outbound_shipment: 'CLAIMS_CENTER_CASH_ON_DELIVERY_IS_NOT_OUTBOUND_SHIPMENT',
};

interface UseSearchShipmentProps {
  claim: ClaimContext;
  changeClaim?: (context: Partial<ClaimContext>) => void;
  resetClaim?: () => void;
}

interface SearchShipmentResult {
  isClaimSubmissionBlocked: boolean;
}

function useSearchShipment({ claim, changeClaim = () => {}, resetClaim = () => {} }: UseSearchShipmentProps) {
  const { showError } = useNotifications();
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [nonClaimableMessage, setNonClaimableMessage] = useState<string | undefined>();
  const shopId = useSelectedShopId();
  const shopSettings = useClaimsShopSettings();
  const { isDORSigningLinkEnabled, isOrderItemLevelTaxEnabled } = shopSettings;

  const resetShipmentSearch = useCallback(() => {
    resetClaim();
    setNonClaimableMessage(undefined);
  }, [resetClaim, setNonClaimableMessage]);

  const loadShipmentByTrackingId = useCallback(
    async (trackingCode: string): Promise<SearchShipmentResult> => {
      setIsSearching(true);
      setNonClaimableMessage(undefined);

      try {
        const response = await container.touchpointsApi.findClaimableShipments(shopId, trackingCode);
        if (response.length > 0) {
          const shipment = response[0];
          const nonClaimableCode =
            shipment.nonClaimableReasonCode ||
            (claim.type ? shipment.claimSubmissionInfo[claim.type]?.block : undefined);

          if (!nonClaimableCode) {
            const flags: Flags = {
              isOrderItemTaxRate: isOrderItemLevelTaxEnabled,
            };

            const context = createClaimContextFromShipment(claim.entryPoint, shipment, flags, shopSettings);
            changeClaim(context);
          }

          setIsSearching(false);
          setNonClaimableMessage(nonClaimableCode ? getNonClaimableMessage(nonClaimableCode) : undefined);

          return { isClaimSubmissionBlocked: !!nonClaimableCode };
        }

        setIsSearching(false);

        return { isClaimSubmissionBlocked: false };
      } catch (err) {
        setIsSearching(false);
        showError();

        return { isClaimSubmissionBlocked: true };
      }
    },
    [changeClaim, setNonClaimableMessage, showError, shopId, claim, isDORSigningLinkEnabled, isOrderItemLevelTaxEnabled]
  );

  return {
    isSearching,
    nonClaimableMessage,
    resetShipmentSearch,
    loadShipmentByTrackingId,
  };
}

function getNonClaimableMessage(code: string): string {
  return nonClaimableMessages[code] || 'Unknown reason';
}

export default useSearchShipment;
